//---------------------------------------------------------------------------------------------------
// ## IMPORTS ##
// Library Imports
import { useEffect, useState } from "react";

// Custom components imports (and hooks and helpers)
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { Box, Breadcrumbs, Typography, useTheme, CircularProgress, Grid2 } from "@mui/material";

import { Params, useNavigate, useParams } from "react-router-dom";
import { useCreateSceneMutation, useGetScenesQuery } from "../../services/scene.service";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { IScene } from "@virtus-tech-repository/virtus-tech-repository/lib/models/scenario.model";

import { formatDateTimePretty, VTTypography } from "@virtus-tech-repository/virtus-tech-repository";

import { BarChart, LineChart, PieChart } from "@mui/x-charts";
import { ITrackingActions, ITrackingHotspotAction } from "../../models/tracking.models";
import { useGetHotspotsQuery } from "../../services/hotspot.service";
import { IHotspot } from "@virtus-tech-repository/virtus-tech-repository/lib/models/hotspot.model";
import VTTimelineItem from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTTimelineItem";
import { getImageForSceneOrDefaultImageIfPreviewImageDoesntExist } from "../../utils/getImageForSceneOrDefaultImageIfPreviewImageDoesntExist";
import { useGetScenarioOverviewQuery } from "../../services/scenario.service";
import ViewMarksContainerTable from "./MarksViewContainerTable";
import NoUser from "../../assets/images/NoScenarios.svg";
import { getTimeElapsedInStopWatchFormatString } from "../../utils/getTimeElapsedInStopWatchFormattedString";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import MouseRoundedIcon from "@mui/icons-material/MouseRounded";
import AdsClickRoundedIcon from "@mui/icons-material/AdsClickRounded";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetUserScoreQuery } from "../../services/tracking.service";
import { useGetMarksQuery } from "../../services/marks.service";
import { ScenesMarks } from "@virtus-tech-repository/virtus-tech-repository/lib/models/marks.model";

import AvTimerRoundedIcon from "@mui/icons-material/AvTimerRounded";

import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";

interface IProps {
    drawWidth: number;
}
//---------------------------------------------------------------------------------------------------
// ## COMPONENTS ##
// Error Page, to display on breaking errors and allows home navigation
export default function VTOverviewUser({ drawWidth }: IProps) {
    //-------------------------------------------------------------------------------------------------
    // ## USE SELECTOR ##
    const theme = useTheme();
    const [currentSceneOverview, setCurrentSceneOverview] = useState<string>("");
    const { selectedAttempt, selectedUser, selectedVersion } = useAppSelector((state) => state.currentReducer);

    function checkIfSelectedAttemptHasUserData(selectedAttempt: any) {
        // selectedAttempt.data.actions.length >= 1 part in expression is redundant if all selectedAttempt.data.actions contain atleast 1 item

        // is it possible to have no hotspotActions data on scene 1 but hotspotActions data on other scenes?

        let totalHotspotActions = selectedAttempt.data.actions.map((action: any) => {
            return action.hotspotActions.length;
        }).length;

        return totalHotspotActions > 0;
    }

    const showUserMetricsForThisScenarioAttempt = checkIfSelectedAttemptHasUserData(selectedAttempt);
    //-------------------------------------------------------------------------------------------------
    // ## USE STATE ##

    //-------------------------------------------------------------------------------------------------
    // ## HOOKS ##
    const { scenarioId, sceneId }: Readonly<Params<string>> = useParams();
    const { data: scenarioOverviewData } = useGetScenarioOverviewQuery(
        selectedVersion ? selectedVersion : scenarioId ? scenarioId : "",
    );

    const [hotspotClicked, setHotspotClicked] = useState<number>(0);
    const [advTimePerScene, setAdvTimePerScene] = useState<number>(0);
    const [scenesLogged, setScenesLogged] = useState<
        { sceneId: string; timeOnScene: number; hotspotsClicked: number; hotspotsHovered: number }[]
    >([]);

    const timeOnSceneInSecs = Math.round(
        scenesLogged.find(
            (findElement: { sceneId: string; timeOnScene: number }) => findElement.sceneId === currentSceneOverview,
        )?.timeOnScene!,
    );

    useEffect(() => {
        if (selectedAttempt) {
            let tempHotspotsClicked = 0;
            let tempSceneTime = 0;
            let tempScenesLogged: {
                sceneId: string;
                timeOnScene: number;
                hotspotsClicked: number;
                hotspotsHovered: number;
            }[] = [];

            selectedAttempt.data.actions.map((sceneElement: any) => {
                if (sceneElement.hotspotActions.length) {
                    const sceneElementTime =
                        Math.abs(
                            new Date(
                                sceneElement.hotspotActions[sceneElement.hotspotActions.length - 1].date,
                            ).getTime() - new Date(sceneElement.date).getTime(),
                        ) / 1000;
                    let sceneElementHotspotClicked = 0;
                    let sceneElementHotspotHovered: string[] = [];

                    tempSceneTime = tempSceneTime + sceneElementTime;

                    sceneElement.hotspotActions.map((actionElement: any) => {
                        if (actionElement.action === "Clicked") {
                            sceneElementHotspotClicked++;
                        }

                        if (
                            actionElement.action === "Hovered" &&
                            !sceneElementHotspotHovered.includes(actionElement.hotspotId)
                        ) {
                            sceneElementHotspotHovered.push(actionElement.hotspotId);
                        }
                    });

                    tempHotspotsClicked = tempHotspotsClicked + sceneElementHotspotClicked;

                    if (
                        tempScenesLogged.filter((filterElement: any) => filterElement.sceneId === sceneElement.sceneId)
                    ) {
                        tempScenesLogged.push({
                            sceneId: sceneElement.sceneId,
                            timeOnScene: sceneElementTime,
                            hotspotsClicked: sceneElementHotspotClicked,
                            hotspotsHovered: sceneElementHotspotHovered.length,
                        });
                    } else {
                        let tempIndex = tempScenesLogged.findIndex(
                            (indexFindElement: any) => indexFindElement.sceneId === sceneElement.sceneId,
                        );
                        tempScenesLogged[tempIndex] = {
                            sceneId: sceneElement.sceneId,
                            timeOnScene: tempScenesLogged[tempIndex].timeOnScene + sceneElementTime,
                            hotspotsClicked: sceneElementHotspotClicked,
                            hotspotsHovered: sceneElementHotspotHovered.length,
                        };
                    }
                }
            });

            setAdvTimePerScene(Math.round(tempSceneTime / selectedAttempt.data.actions.length));
            setHotspotClicked(tempHotspotsClicked);
            setScenesLogged(tempScenesLogged);

            if (scenarioOverviewData && scenarioOverviewData.scenes) {
                setCurrentSceneOverview(
                    [...scenarioOverviewData.scenes]?.sort((element1: IScene, element2: IScene) => {
                        if (element1.order === undefined) {
                            return 1;
                        } else if (element2.order === undefined) {
                            return -1;
                        } else {
                            return element2.order < element1.order ? 1 : -1;
                        }
                    })[0].id,
                );
            }
        }
    }, [selectedAttempt, scenarioOverviewData]);

    const { data: marksData, refetch: refetchMarks } = useGetMarksQuery(
        selectedVersion ? selectedVersion : scenarioId!,
        {
            // pollingInterval: 5000, // get marks from server every 5secs
        },
    );

    const [weightedHotspots, setWeightedHotspots] = useState<{ id: string; weighting: number }[]>([]);
    const [totalMarks, setTotalMarks] = useState<number>(0);
    const [userScore, setUserScore] = useState<number | undefined>(undefined);

    const nOfClickedHotspotsForCurrentScene = scenesLogged.find(
        (findElement: { sceneId: string; timeOnScene: number }) => findElement.sceneId === currentSceneOverview,
    )?.hotspotsClicked!;

    const nOfHoveredHotspotsForCurrentScene = scenesLogged.find(
        (findElement: { sceneId: string; timeOnScene: number }) => findElement.sceneId === currentSceneOverview,
    )?.hotspotsHovered!;

    useEffect(() => {
        let tempWeightedHotspots: { id: string; weighting: number }[] = [];
        let tempTotalMarks: number = 0;
        marksData?.forEach((element: ScenesMarks) => {
            element.sceneHotspots.forEach((subElement: any) => {
                if (subElement.hotspotType === "question") {
                    subElement.answerHotspots.forEach((microElement: any) => {
                        if (microElement.marked) {
                            tempWeightedHotspots.push({
                                id: microElement.hotspotId,
                                weighting: microElement.weighting,
                            });
                            tempTotalMarks = tempTotalMarks + microElement.weighting;
                        }
                    });
                } else if (subElement.marked) {
                    tempWeightedHotspots.push({ id: subElement.hotspotId, weighting: subElement.weighting });
                    tempTotalMarks = tempTotalMarks + subElement.weighting;
                }
            });
        });
        setTotalMarks(tempTotalMarks);
        setWeightedHotspots(tempWeightedHotspots);
    }, [marksData]);

    useEffect(() => {
        if (selectedAttempt && weightedHotspots.length > 0) {
            let tempUserScore: number = 0;
            let TempSceneList: { sceneId: string; correct: boolean }[] = [];

            selectedAttempt.data.actions.forEach((element: any) => {
                let tempFirstAnswer: { sceneId: string; correct: boolean } | undefined = undefined;
                let tempFirstStopPointAnswer: { sceneId: string; questionId: string; correct: boolean }[] = [];

                element.hotspotActions.forEach((subElement: any) => {
                    if (subElement.action === "Clicked" && subElement.questionId) {
                        const tempFirstStopPointAnswerIncluded = tempFirstStopPointAnswer.find(
                            (findElement: { questionId: string; correct: boolean }) =>
                                findElement.questionId === subElement.questionId,
                        );
                        if (tempFirstStopPointAnswerIncluded === undefined) {
                            let actionWeighting = weightedHotspots.find(
                                (findElement: { id: string; weighting: number }) =>
                                    findElement.id === subElement.hotspotId,
                            );
                            if (actionWeighting !== undefined) {
                                tempFirstStopPointAnswer.push({
                                    sceneId: element.sceneId,
                                    questionId: subElement.questionId,
                                    correct: actionWeighting.weighting > 0,
                                });
                                tempUserScore = tempUserScore + actionWeighting.weighting;
                            }
                        }
                    } else if (subElement.action === "Clicked" && !subElement.questionId && !tempFirstAnswer) {
                        let actionWeighting = weightedHotspots.find(
                            (findElement: { id: string; weighting: number }) => findElement.id === subElement.hotspotId,
                        );
                        if (actionWeighting !== undefined) {
                            tempFirstAnswer = { sceneId: element.sceneId, correct: actionWeighting.weighting > 0 };
                            tempUserScore = tempUserScore + actionWeighting.weighting;
                        }
                    }
                });

                if (tempFirstAnswer) {
                    TempSceneList.push(tempFirstAnswer);
                }
                if (tempFirstStopPointAnswer.length > 0 && !tempFirstAnswer) {
                    TempSceneList.push({ sceneId: element.sceneId, correct: true });
                }
            });

            setUserScore(tempUserScore);
        }
    }, [weightedHotspots, selectedAttempt]);

    const breadCrumbsUserAttempt = [
        <Typography
            variant="h5"
            //  borderBottom: "2px solid white"
            sx={
                {
                    // display: "inline",
                }
            }
        >
            {selectedUser.email}
        </Typography>,
        <Typography
            variant="h5"
            //  borderBottom: "2px solid white"
            sx={
                {
                    // display: "inline",
                }
            }
        >
            {formatDateTimePretty(new Date(selectedAttempt.data.date))}
        </Typography>,
    ];

    //-------------------------------------------------------------------------------------------------
    // ## RETURN ##
    // Displays return button and error message
    return (
        <>
            {selectedAttempt && (
                <Grid2 container rowSpacing={2} columnSpacing={2}>
                    <Grid2 container size={12} sx={{}}>
                        <Breadcrumbs
                            sx={{ marginBottom: "10px", color: "#ccc" }}
                            separator={<NavigateNextIcon fontSize="medium" />}
                            aria-label="breadcrumb"
                        >
                            {breadCrumbsUserAttempt}
                        </Breadcrumbs>
                    </Grid2>

                    <Grid2 container size={12}>
                        <Grid2
                            size={7}
                            container
                            sx={{
                                backgroundColor: "#393939",
                                borderRadius: "15px",
                                position: "relative",
                            }}
                        >
                            <VTTypography sx={{ position: "absolute", top: "10px", left: "14px", color: "#aaa" }}>
                                Activity per scene
                            </VTTypography>
                            {selectedAttempt && selectedAttempt.data && (
                                // <LineChart
                                //     // sx={{ border: "2px solid white" }}
                                //     series={[
                                //         {
                                //             data: [
                                //                 ...selectedAttempt.data.actions.map(
                                //                     (attemptAction: any) =>
                                //                         attemptAction.hotspotActions.filter(
                                //                             (filterElement: ITrackingHotspotAction) =>
                                //                                 filterElement.action !== "Hovered",
                                //                         ).length + 1,
                                //                 ),
                                //             ],
                                //             area: true,
                                //             curve: "linear",
                                //             color: theme.palette.primary.light,
                                //         },
                                //     ]}
                                //     // sx={{ width: "100%" }}
                                //     height={220}
                                // />
                                <BarChart
                                    // xAxis={[{ scaleType: "band", data: ["group A", "group B", "group C"] }]}
                                    series={[
                                        {
                                            data: [
                                                ...selectedAttempt.data.actions.map(
                                                    (attemptAction: any) =>
                                                        attemptAction.hotspotActions.filter(
                                                            (filterElement: ITrackingHotspotAction) =>
                                                                filterElement.action !== "Hovered",
                                                        ).length + 1,
                                                ),
                                            ],

                                            color: theme.palette.primary.light,
                                        },
                                    ]} // width={500}
                                    height={300}
                                />
                            )}
                        </Grid2>

                        <Grid2
                            size={5}
                            container

                            // columnSpacing={2}
                            // item
                            // sx={{ backgroundColor: "red" }}
                        >
                            <Grid2
                                size={6}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px" }}
                                // container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                {/* <Grid2 item> */}
                                <Grid2 sx={{ textAlign: "center" }}>
                                    <HourglassEmptyRoundedIcon sx={{ fontSize: "2.5em" }} />
                                </Grid2>
                                <VTTypography align="center">Total Session time</VTTypography>
                                {/* </Grid2> */}
                                {/* <Grid2 item> */}
                                <VTTypography align="center" type={"Heading3"}>
                                    {Math.floor(
                                        Math.round((advTimePerScene * selectedAttempt.data.actions.length) / 60),
                                    )}
                                    m {Math.round((advTimePerScene * selectedAttempt.data.actions.length) % 60)}s
                                </VTTypography>
                                {/* </Grid2> */}
                            </Grid2>

                            <Grid2
                                size={6}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px" }}
                                // container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                {/* <Grid2 item> */}
                                <Grid2 sx={{ textAlign: "center" }}>
                                    <MouseRoundedIcon sx={{ fontSize: "2.5em" }} />
                                </Grid2>
                                <VTTypography align="center">Hotspots Clicked</VTTypography>
                                {/* </Grid2> */}
                                {/* <Grid2 item> */}
                                <VTTypography align="center" type={"Heading3"}>
                                    {hotspotClicked}
                                </VTTypography>
                                {/* </Grid2> */}
                            </Grid2>

                            <Grid2
                                size={6}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px" }}
                                // container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid2 sx={{ textAlign: "center" }}>
                                    <AvTimerRoundedIcon sx={{ fontSize: "2.5em" }} />
                                </Grid2>
                                <VTTypography align="center">Avg time per scene</VTTypography>
                                <VTTypography align="center" type={"Heading3"}>
                                    {}
                                    {Math.floor(Math.round(advTimePerScene / 60))}m {Math.round(advTimePerScene % 60)}s
                                </VTTypography>
                            </Grid2>

                            <Grid2
                                size={6}
                                sx={{ backgroundColor: "#393939", borderRadius: "15px" }}
                                // container
                                justifyContent={"center"}
                                alignContent={"center"}
                                direction={"column"}
                            >
                                <Grid2 sx={{ textAlign: "center" }}>
                                    <RemoveRedEyeRoundedIcon sx={{ fontSize: "2.5em" }} />
                                </Grid2>
                                <VTTypography align="center">Scenes viewed</VTTypography>
                                <VTTypography align="center" type={"Heading3"}>
                                    {
                                        scenesLogged.filter(
                                            (
                                                filterElement: { sceneId: string; timeOnScene: number },
                                                filterIndex: number,
                                            ) =>
                                                filterIndex ===
                                                scenesLogged.findIndex(
                                                    (findElement: { sceneId: string; timeOnScene: number }) =>
                                                        findElement.sceneId === filterElement.sceneId,
                                                ),
                                        ).length
                                    }
                                    {scenarioOverviewData &&
                                        scenarioOverviewData.scenes &&
                                        "/" + scenarioOverviewData.scenes.length}
                                </VTTypography>
                            </Grid2>
                        </Grid2>
                    </Grid2>

                    <Grid2 container size={12}>
                        <Grid2 container size={8}>
                            <Grid2 size={12} sx={{ borderBottom: "2px solid #ccc" }}>
                                <Typography sx={{ color: "#ccc" }} variant="subtitle1">
                                    {`Scene Data ${
                                        currentSceneOverview
                                            ? "- " +
                                              scenarioOverviewData?.scenes.find(
                                                  (scene) => currentSceneOverview === scene.id,
                                              )?.name
                                            : ""
                                    }`}
                                </Typography>
                            </Grid2>

                            <Box
                                component="div"
                                sx={{
                                    padding: "15px",
                                    // marginTop: "8px",
                                    // overflow: "",
                                    backgroundColor: "#393939",
                                    height: "260px",
                                    overflow: "scroll",
                                    borderRadius: "15px",
                                }}
                            >
                                {scenarioOverviewData &&
                                    scenarioOverviewData.scenes &&
                                    [...scenarioOverviewData.scenes]
                                        ?.sort((element1: IScene, element2: IScene) => {
                                            if (element1.order === undefined) {
                                                return 1;
                                            } else if (element2.order === undefined) {
                                                return -1;
                                            } else {
                                                return element2.order < element1.order ? 1 : -1;
                                            }
                                        })
                                        .map((sceneElement: IScene) => (
                                            // <Grid2
                                            //     item
                                            //     size={12}
                                            // container
                                            //     justifyContent={"center"}
                                            //     // sx={{ marginBottom: "10px" }}
                                            // >
                                            <VTTimelineItem
                                                mediaType={sceneElement.mediaType}
                                                image={getImageForSceneOrDefaultImageIfPreviewImageDoesntExist(
                                                    sceneElement,
                                                )}
                                                name={sceneElement.name}
                                                disableHover
                                                active={currentSceneOverview === sceneElement.id}
                                                onClicked={() => {
                                                    setCurrentSceneOverview(sceneElement.id);
                                                }}
                                                titleCutOffLimit={6}
                                                // sx={{ width: "200px", height: "170px" }}
                                                sx={{
                                                    // marginBottom: "-15px",
                                                    marginBottom: "6px",
                                                }}
                                                width={110}
                                                height={75}
                                            />
                                            // </Grid2>
                                        ))}
                            </Box>

                            <Box component="div" sx={{ flexGrow: 1 }}>
                                <Grid2 container columnSpacing={2} size={12}>
                                    {currentSceneOverview !== "" ? (
                                        <>
                                            <Grid2
                                                size={4}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    // padding: "15px",
                                                    height: "200px",
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid2 sx={{ textAlign: "center" }}>
                                                    <AccessTimeRoundedIcon sx={{ fontSize: "2.5em" }} />
                                                </Grid2>

                                                {/* <Grid2 item> */}
                                                <VTTypography align="center">
                                                    Time on <br /> Scene
                                                </VTTypography>
                                                {/* </Grid2> */}
                                                {/* <Grid2 item> */}
                                                <VTTypography align="center" type={"Heading3"}>
                                                    {timeOnSceneInSecs
                                                        ? `${Math.floor(timeOnSceneInSecs / 60)}m ${
                                                              timeOnSceneInSecs % 60
                                                          }s`
                                                        : "-"}
                                                </VTTypography>
                                                {/* </Grid2> */}
                                            </Grid2>

                                            <Grid2
                                                size={4}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    // padding: "15px"
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid2 sx={{ textAlign: "center" }}>
                                                    <MouseRoundedIcon sx={{ fontSize: "2.5em" }} />
                                                </Grid2>
                                                {/* <Grid2 item> */}
                                                <VTTypography align="center">
                                                    Hotspots <br /> Clicked
                                                </VTTypography>
                                                {/* </Grid2> */}
                                                {/* <Grid2 item> */}
                                                <VTTypography align="center" type={"Heading3"}>
                                                    {nOfClickedHotspotsForCurrentScene || "-"}
                                                </VTTypography>
                                                {/* </Grid2> */}
                                            </Grid2>
                                            <Grid2
                                                size={4}
                                                sx={{
                                                    backgroundColor: "#393939",
                                                    borderRadius: "15px",
                                                    // padding: "15px"
                                                }}
                                                container
                                                justifyContent={"center"}
                                                alignContent={"center"}
                                                direction={"column"}
                                            >
                                                <Grid2 sx={{ textAlign: "center" }}>
                                                    <AdsClickRoundedIcon sx={{ fontSize: "2.5em" }} />
                                                </Grid2>
                                                {/* <Grid2 item> */}
                                                <VTTypography align="center">
                                                    Hotspots <br /> Hovered
                                                </VTTypography>
                                                {/* </Grid2> */}
                                                {/* <Grid2 item> */}
                                                <VTTypography align="center" type={"Heading3"}>
                                                    {nOfHoveredHotspotsForCurrentScene || "-"}
                                                </VTTypography>
                                                {/* </Grid2> */}
                                            </Grid2>
                                        </>
                                    ) : (
                                        <>
                                            {/* <Grid2 item size={12} container justifyContent={"center"}> */}
                                            <VTTypography type={"Heading3"} sx={{ marginTop: "30px" }}>
                                                No scene selected
                                            </VTTypography>
                                            {/* </Grid2> */}

                                            {/* <Grid2 item size={12} container justifyContent={"center"}> */}
                                            <VTTypography type={"Heading4"}>
                                                Please select a scene from the panel on the left
                                            </VTTypography>
                                            {/* </Grid2> */}
                                        </>
                                    )}
                                </Grid2>
                            </Box>
                        </Grid2>

                        <Grid2
                            container
                            justifyContent="center"
                            alignItems="center"
                            size={4}
                            sx={{
                                backgroundColor: "#393939",
                                borderRadius: "15px",
                                position: "relative",
                            }}
                        >
                            <VTTypography sx={{ position: "absolute", top: "10px", left: "14px", color: "#aaa" }}>
                                {`user score ${userScore}/${totalMarks}`}
                            </VTTypography>
                            {userScore === undefined ? (
                                // <>
                                <CircularProgress thickness={4} />
                            ) : // </>
                            totalMarks > 0 ? (
                                <Box component="div" sx={{ height: "120px", flexGrow: 1 }}>
                                    <PieChart
                                        sx={{}}
                                        series={[
                                            {
                                                data: [
                                                    {
                                                        id: 0,
                                                        value: userScore,
                                                        label: "correct",
                                                        color: theme.palette.primary.light,
                                                    },
                                                    {
                                                        id: 1,
                                                        value: totalMarks - userScore,
                                                        label: "incorrect",
                                                        color: "#555",
                                                    },
                                                    // { id: 2, value: 20, label: "series C" },
                                                ],
                                                // outerRadius: 30,
                                            },
                                        ]}
                                    />
                                </Box>
                            ) : (
                                <Typography> no marks for this user yet</Typography>
                            )}
                        </Grid2>
                    </Grid2>

                    {/* <Grid2
                        item
                        container
                        size={3}
                        sx={{
                            height: `calc(100vh - 145px - ${drawWidth}px)`,
                            overflow: "auto",
                            backgroundColor: "#393939",
                            borderRadius: "15px",
                            padding: "10px",
                            marginTop: "3px",
                        }}
                    >
                        <ViewMarksContainerTable drawWidth={drawWidth} />
                    </Grid2> */}
                </Grid2>
            )}
        </>
    );
}
